import { MemberPermission } from "@/components/portfolio/settings/types/memberTypes";
import { db } from "@/utils/firebase";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";

/**
 * Add a user to a business
 * @param userId The ID of the user to add
 * @param businessId The ID of the business to add the user to
 * @param role The role of the user in the business
 * @param permissions The permissions of the user in the business
 * @param firstName The first name of the user
 * @param lastName The last name of the user
 * @param email The email of the user
 * @param phone The phone number of the user
 */
export const addUserToBusiness = async (
  userId: string,
  businessId: string,
  role: string,
  permissions: MemberPermission[] = [],
  firstName: string = "",
  lastName: string = "",
  email: string = "",
  phone: string = ""
): Promise<boolean> => {
  try {
    console.log(`Adding user ${userId} to business ${businessId} with role ${role}`);
    
    // Get the business document
    const businessRef = doc(db, "businesses", businessId);
    const businessDoc = await getDoc(businessRef);
    
    if (!businessDoc.exists()) {
      console.error(`Business ${businessId} does not exist`);
      return false;
    }
    
    // Create the member object
    const member = {
      userId,
      role,
      permissions,
      status: "active",
      joinedAt: new Date().toISOString(),
      firstName,
      lastName,
      email,
      phone,
      // Important: addedViaPortfolio is null to indicate this user was added directly to the business
      addedViaPortfolio: null
    };
    
    // Check if the business has a members array
    const businessData = businessDoc.data();
    const members = businessData.members || [];
    
    // Define a type for the member object
    interface BusinessMember {
      userId: string;
      role?: string;
      permissions?: MemberPermission[];
      status?: string;
      joinedAt?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      addedViaPortfolio?: string | null;
      // Use more specific types instead of any
      [key: string]: string | string[] | MemberPermission[] | null | undefined;
    }
    
    // Check if the user is already a member
    const existingMemberIndex = members.findIndex((m: BusinessMember) => m.userId === userId);
    
    if (existingMemberIndex >= 0) {
      // Update the existing member
      members[existingMemberIndex] = {
        ...members[existingMemberIndex],
        ...member
      };
      
      // Update the business document with the updated members array
      await updateDoc(businessRef, { members });
    } else {
      // Add the new member to the members array
      await updateDoc(businessRef, {
        members: arrayUnion(member)
      });
    }
    
    console.log(`Successfully added user ${userId} to business ${businessId}`);
    return true;
  } catch (error) {
    console.error("Error adding user to business:", error);
    return false;
  }
};
